import { CheckCircle2, CircleDot, Flag, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { concernCount, concernName } from "./run-report-dialog/item-status";
import type { ItemSummary, RunSummary } from "./types";

const SUMMARY_STATES = {
  "in-progress": {
    label: "In progress",
    icon: CircleDot,
    style: "bg-blue-50 text-blue-800",
  },
  "attention-needed": {
    label: "Needs attention",
    icon: TriangleAlert,
    style: "bg-amber-50 text-amber-900",
  },
  limited: {
    label: "Collected with limitations",
    icon: TriangleAlert,
    style: "bg-amber-50 text-amber-900",
  },
  completed: {
    label: "Completed",
    icon: CheckCircle2,
    style: "bg-slate-100 text-slate-700",
  },
  incomplete: {
    label: "Incomplete",
    icon: TriangleAlert,
    style: "bg-amber-50 text-amber-900",
  },
  "no-issues-found": {
    label: "No issues found",
    icon: CheckCircle2,
    style: "bg-green-50 text-green-800",
  },
} as const;

export function RunStatusBadge({ summary }: { summary: RunSummary }) {
  const state = SUMMARY_STATES[summary.status];
  const Icon = state.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium",
        state.style,
      )}
    >
      <Icon className="size-3.5" aria-hidden="true" />
      {state.label}
    </span>
  );
}

export function runFindingLabels(summary: RunSummary) {
  return [
    {
      count: summary.expiredLicenses,
      one: "expired credential",
      many: "expired credentials",
    },
    {
      count: summary.invalidLicenses,
      one: "invalid or restricted credential",
      many: "invalid or restricted credentials",
    },
    {
      count: summary.possibleExclusions,
      one: "possible exclusion",
      many: "possible exclusions",
    },
    {
      count: summary.possibleSanctions,
      one: "potential sanctions match",
      many: "potential sanctions matches",
    },
    {
      count: summary.medicareParticipationFindings,
      one: "Medicare participation finding",
      many: "Medicare participation findings",
    },
    {
      count: summary.incompleteChecks,
      one: "incomplete check",
      many: "incomplete checks",
    },
    {
      count: summary.limitedChecks,
      one: "check with limitations",
      many: "checks with limitations",
    },
    {
      count: summary.otherFindings,
      one: "other finding",
      many: "other findings",
    },
  ]
    .filter(({ count }) => count > 0)
    .map(({ count, one, many }) => `${count} ${count === 1 ? one : many}`);
}

export function RunFindingSummary({ summary }: { summary: RunSummary }) {
  const findings = runFindingLabels(summary);
  return (
    <p className="text-xs leading-5 text-muted-foreground">
      {findings.length > 0
        ? findings.join(" · ")
        : `${summary.collectedChecks} of ${summary.totalChecks} checks collected`}
      {summary.pendingChecks > 0 && ` · ${summary.pendingChecks} pending`}
    </p>
  );
}

/** How far staff review of the run's items has come. */
/** The items staff marked as a concern, by name. */
export function RunConcerns({ summary }: { summary: ItemSummary }) {
  if (!summary.concerns.length) {
    return null;
  }
  return (
    <p className="flex items-start gap-1.5 text-xs leading-5 text-red-800">
      <Flag className="mt-1 size-3 shrink-0" aria-hidden="true" />
      <span>
        <span className="font-medium">Concern:</span>{" "}
        {summary.concerns.map(concernName).join(", ")}
      </span>
    </p>
  );
}

export function RunItemProgress({ summary }: { summary: ItemSummary }) {
  if (summary.total === 0) {
    return null;
  }
  if (summary.complete && summary.concern === 0) {
    return (
      <p className="flex items-center gap-1.5 text-xs font-medium text-emerald-800">
        <CheckCircle2 className="size-3.5" aria-hidden="true" />
        Verification complete
      </p>
    );
  }
  return (
    <div className="space-y-0.5">
      <p className="text-xs leading-5 text-muted-foreground">
        {summary.complete
          ? `Review complete · ${concernCount(summary.concern)}`
          : `${summary.accepted + summary.concern} of ${summary.total} items reviewed`}
      </p>
      {summary.carried > 0 && (
        <p className="text-xs leading-5 text-muted-foreground">
          {summary.carried} carried forward
        </p>
      )}
      <RunConcerns summary={summary} />
    </div>
  );
}
